import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { Product } from "../../../types";
import { productService } from "../../../services/productService";
import { ProductThumbnail } from "../../../components/common/ProductThumbnail";
import { priceLine, stockWords } from "../answerDeskUtils";

interface ProductPickerProps {
    value: Product[];
    onChange: (products: Product[]) => void;
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    /** Max products that can be picked. */
    max?: number;
}

// The Answer Desk's own picker: a shorter debounce than the shared
// ProductSearchAutocomplete (moderators are mid-chat) and price/stock in
// each option, without changing the shared component other pages use.
export const ProductPicker: React.FC<ProductPickerProps> = ({
    value,
    onChange,
    label = "Products in this chat",
    placeholder = "Search a product by name or barcode…",
    size = "medium",
    max = 10,
}) => {
    const [input, setInput] = useState("");
    const [options, setOptions] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const term = input.trim();
        if (term.length < 2) {
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const timer = setTimeout(async () => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            try {
                const res = await productService.getProducts({ search: term, limit: 20, include_stock: true });
                if (!controller.signal.aborted) setOptions(res.data ?? res.items ?? []);
            } catch {
                if (!controller.signal.aborted) setOptions([]);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [input]);

    useEffect(() => () => abortRef.current?.abort(), []);

    return (
        <Autocomplete
            multiple
            size={size}
            value={value}
            options={options}
            filterOptions={(x) => x}
            loading={loading}
            inputValue={input}
            onInputChange={(_, v, reason) => {
                if (reason !== "reset") setInput(v);
            }}
            onChange={(_, products) => {
                onChange(products.slice(0, max));
                setInput("");
            }}
            isOptionEqualToValue={(a, b) => a.product_id === b.product_id}
            getOptionLabel={(p) => p.name}
            noOptionsText={input.trim().length < 2 ? "Type at least 2 letters" : "No products found"}
            renderTags={(selected, getTagProps) =>
                selected.map((p, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} label={p.name} size="small" {...tagProps} sx={{ maxWidth: 260 }} />;
                })
            }
            renderOption={(props, p) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                return (
                    <Box component="li" key={key} {...rest} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <ProductThumbnail images={p.images} size={36} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                                {p.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {priceLine(p)} · {stockWords(p)}
                                {p.brand?.name ? ` · ${p.brand.name}` : ""}
                            </Typography>
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={value.length === 0 ? placeholder : "Add another…"}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
